/**
 * Game validation logic that can be shared between client and server
 * This should be deployed to Firebase Functions for server-side validation
 */

import { Position, Unit, FactionType, GameState } from './types';

export interface ValidationResult {
  valid: boolean;
  reason?: string;
}

/**
 * Validate a move action
 */
export function validateMove(
  unit: Unit,
  targetPosition: Position,
  gameState: GameState
): ValidationResult {
  // Check if it's the unit's faction's turn
  if (unit.faction !== gameState.currentFaction) {
    return { valid: false, reason: 'Not this faction\'s turn' };
  }

  // Check if unit has movement remaining
  if (!unit.movementRemaining || unit.movementRemaining <= 0) {
    return { valid: false, reason: 'No movement remaining' };
  }

  // Check if target position is within bounds
  if (targetPosition.x < 0 || targetPosition.y < 0) {
    return { valid: false, reason: 'Invalid target position' };
  }

  // Check if movement distance is valid
  const distance = Math.abs(targetPosition.x - unit.position.x) + 
                  Math.abs(targetPosition.y - unit.position.y);
  
  if (distance > unit.movementRemaining) {
    return { valid: false, reason: 'Target position too far' };
  }

  return { valid: true };
}

/**
 * Validate an attack action
 */
export function validateAttack(
  attacker: Unit,
  target: Unit,
  gameState: GameState
): ValidationResult {
  // Check if it's the attacker's faction's turn
  if (attacker.faction !== gameState.currentFaction) {
    return { valid: false, reason: 'Not this faction\'s turn' };
  }

  // Check if attacker has already attacked
  if (attacker.hasAttacked) {
    return { valid: false, reason: 'Unit has already attacked this turn' };
  }

  // Check if target is enemy
  if (attacker.faction === target.faction) {
    return { valid: false, reason: 'Cannot attack friendly units' };
  }

  // Check if target is alive
  if (target.stats.health <= 0) {
    return { valid: false, reason: 'Target is already destroyed' };
  }

  // Check attack range
  const distance = Math.abs(target.position.x - attacker.position.x) + 
                  Math.abs(target.position.y - attacker.position.y);
  
  if (distance > attacker.stats.range) {
    return { valid: false, reason: 'Target out of range' };
  }

  return { valid: true };
}

/**
 * Validate build/training action
 */
export function validateBuild(
  unitType: string,
  faction: FactionType,
  resources: number,
  unitCost: number,
  gameState: GameState
): ValidationResult {
  // Check if it's the faction's turn
  if (faction !== gameState.currentFaction) {
    return { valid: false, reason: 'Not this faction\'s turn' };
  }

  // Check if faction has enough resources
  if (resources < unitCost) {
    return { valid: false, reason: 'Insufficient resources' };
  }

  // Validate unit type exists
  const validUnitTypes = [
    'infantry', 'mechanized', 'armor', 'artillery', 
    'reconnaissance', 'aircraft', 'engineers', 'medics'
  ];
  
  if (!validUnitTypes.some(type => unitType.toLowerCase().includes(type))) {
    return { valid: false, reason: 'Invalid unit type' };
  }

  return { valid: true };
}

/**
 * Validate turn order
 */
export function validateTurnOrder(
  actionFaction: FactionType,
  currentTurn: FactionType
): ValidationResult {
  if (actionFaction !== currentTurn) {
    return { 
      valid: false, 
      reason: `It's ${currentTurn}'s turn, not ${actionFaction}'s` 
    };
  }
  
  return { valid: true };
}

/**
 * Validate game state integrity
 */
export function validateGameStateIntegrity(gameState: any): ValidationResult {
  // Check required fields
  if (!gameState.units || !gameState.resources || !gameState.currentFaction) {
    return { valid: false, reason: 'Missing required game state fields' };
  }

  // Check turn number
  if (typeof gameState.turn !== 'number' || gameState.turn < 0) {
    return { valid: false, reason: 'Invalid turn number' };
  }

  // Validate faction
  const validFactions = [
    'United States Of America',
    '3rd Reich',
    'United Kingdom',
    'USSR',
    'Imperial Japan',
    'Canada',
    'Fascist Italy'
  ];

  if (!validFactions.includes(gameState.currentFaction)) {
    return { valid: false, reason: 'Invalid faction' };
  }

  // Check for resource tampering
  for (const [, amount] of Object.entries(gameState.resources)) {
    if (typeof amount !== 'number' || amount < 0 || amount > 99999) {
      return { valid: false, reason: 'Invalid resource values detected' };
    }
  }

  return { valid: true };
}

/**
 * Main validation function for any game action
 */
export function validateGameAction(action: any, gameState: GameState): ValidationResult {
  // First validate game state integrity
  const stateValidation = validateGameStateIntegrity(gameState);
  if (!stateValidation.valid) {
    return stateValidation;
  }

  // Validate based on action type
  switch (action.type) {
    case 'move':
      if (!action.unit || !action.targetPosition) {
        return { valid: false, reason: 'Invalid move action structure' };
      }
      return validateMove(
        action.unit,
        action.targetPosition,
        gameState
      );

    case 'attack':
      if (!action.attacker || !action.target) {
        return { valid: false, reason: 'Invalid attack action structure' };
      }
      return validateAttack(action.attacker, action.target, gameState);

    case 'build':
      if (!action.cityPosition || !action.unitType || !action.cost) {
        return { valid: false, reason: 'Invalid build action structure' };
      }
      return validateBuild(
        action.unitType,
        action.faction,
        gameState.resources.get(action.faction) || 0,
        action.cost,
        gameState
      );

    case 'endTurn':
      return validateTurnOrder(action.faction, gameState.currentFaction);

    default:
      return { valid: false, reason: 'Unknown action type' };
  }
}

/**
 * Anti-cheat validation checks
 */
export class AntiCheatValidator {
  private actionHistory: Map<string, any[]> = new Map();
  private readonly MAX_ACTIONS_PER_TURN = 100;
  private readonly MIN_ACTION_INTERVAL_MS = 50;

  /**
   * Check for suspicious patterns
   */
  checkForCheating(playerId: string, action: any): ValidationResult {
    const now = Date.now();
    
    // Get player's action history
    if (!this.actionHistory.has(playerId)) {
      this.actionHistory.set(playerId, []);
    }
    
    const history = this.actionHistory.get(playerId)!;
    
    // Check for too many actions in a turn
    const recentActions = history.filter(a => a.turn === action.turn);
    if (recentActions.length > this.MAX_ACTIONS_PER_TURN) {
      return { 
        valid: false, 
        reason: 'Too many actions in a single turn - possible automation detected' 
      };
    }
    
    // Check for superhuman speed (actions too fast)
    const lastAction = history[history.length - 1];
    if (lastAction && (now - lastAction.timestamp) < this.MIN_ACTION_INTERVAL_MS) {
      return { 
        valid: false, 
        reason: 'Actions submitted too quickly - possible automation detected' 
      };
    }
    
    // Check for impossible movements (teleportation)
    if (action.type === 'move' && lastAction && lastAction.type === 'move') {
      const distance = Math.abs(action.position.x - lastAction.position.x) + 
                      Math.abs(action.position.y - lastAction.position.y);
      
      if (distance > 20) { // Max reasonable movement in one action
        return { 
          valid: false, 
          reason: 'Impossible movement detected - possible position tampering' 
        };
      }
    }
    
    // Add action to history
    history.push({
      ...action,
      timestamp: now
    });
    
    // Keep history size manageable
    if (history.length > 1000) {
      history.shift();
    }
    
    return { valid: true };
  }
  
  /**
   * Reset history for a new game
   */
  reset(): void {
    this.actionHistory.clear();
  }
}